import { useState, useEffect, useId } from 'react';
import { Check } from 'lucide-react';

// ─── Browser frame ─────────────────────────────────────────────────────────────

export function BrowserMockup({ children, contentHeight = 400 }: { children: React.ReactNode; contentHeight?: number }) {
  return (
    <div className="w-full rounded-xl overflow-hidden border border-border" style={{ boxShadow: '0 32px 80px -20px rgba(0,0,0,0.2)' }}>
      <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-background-elevated border-b border-border flex-shrink-0">
        <div className="w-2.5 h-2.5 rounded-full bg-error/50" />
        <div className="w-2.5 h-2.5 rounded-full bg-warning/50" />
        <div className="w-2.5 h-2.5 rounded-full bg-success/50" />
        <div className="flex-1 mx-3 h-5 rounded-md bg-background border border-border flex items-center px-2.5 gap-1.5">
          <div className="w-2 h-2 rounded-full bg-success/50" />
          <span className="text-[9px] text-text-muted">wedsy.co/app/table-plan</span>
        </div>
      </div>
      <div className="browser-content overflow-hidden" style={{ height: contentHeight }}>
        {children}
      </div>
    </div>
  );
}

// ─── Canvas SVG ─────────────────────────────────────────────────────────────────

const C_TOP = { x: 135, y: 28, w: 250, h: 44 };
const C_TOP_X = [165, 260, 355];
const C_ROUNDS = [
  { cx: 90,  cy: 162, id: 1, label: 'Table 1' },
  { cx: 260, cy: 162, id: 2, label: 'Table 2' },
  { cx: 430, cy: 162, id: 3, label: 'Table 3' },
  { cx: 175, cy: 295, id: 4, label: 'Table 4' },
  { cx: 345, cy: 295, id: 5, label: 'Table 5' },
];
const C_R = 38;
const C_SD = 57;
const C_SR = 9;

function cSeats(cx: number, cy: number, n = 6) {
  return Array.from({ length: n }, (_, i) => {
    const a = (2 * Math.PI / n) * i - Math.PI / 2;
    return { x: cx + C_SD * Math.cos(a), y: cy + C_SD * Math.sin(a) };
  });
}

export interface SeatFill { tableId: number; seat: number; initials: string; warn?: boolean }

export function CanvasSvg({ selectedId, targetSeat, filled = [] }: {
  selectedId?: number; targetSeat?: number; filled?: SeatFill[];
}) {
  const uid = useId().replace(/:/g, '');
  const pid = `cvg-${uid}`;
  const filledMap = new Map(filled.map(f => [`${f.tableId}-${f.seat}`, f]));

  return (
    <svg viewBox="0 0 520 360" width="100%" height="100%" style={{ display: 'block', background: '#faf8f5' }}>
      <defs>
        <pattern id={pid} width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#e8e2d9" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect width="520" height="360" fill={`url(#${pid})`} />

      {/* Top Table */}
      <rect x={C_TOP.x} y={C_TOP.y} width={C_TOP.w} height={C_TOP.h} rx={5}
        fill="#f5f0eb" stroke="#c9b99a" strokeWidth="1.5" />
      <text x={C_TOP.x + C_TOP.w / 2} y={C_TOP.y + C_TOP.h / 2 + 4}
        textAnchor="middle" fontSize="10" fontWeight="600" fill="#9e8e7a" fontFamily="sans-serif">Top Table</text>
      {C_TOP_X.map((x, i) => {
        const tF = filledMap.get(`0-${i}`);
        const bF = filledMap.get(`0-${i + 3}`);
        return (
          <g key={i}>
            <circle cx={x} cy={C_TOP.y - 12} r={C_SR} fill={tF ? '#eaf2ea' : '#fff'} stroke={tF ? '#4a7a4a' : '#c9b99a'} strokeWidth="1.5" />
            {tF && <text x={x} y={C_TOP.y - 12 + 3.5} textAnchor="middle" fontSize="6" fontWeight="700" fill="#2d5a2d" fontFamily="sans-serif">{tF.initials}</text>}
            <circle cx={x} cy={C_TOP.y + C_TOP.h + 12} r={C_SR} fill={bF ? '#eaf2ea' : '#fff'} stroke={bF ? '#4a7a4a' : '#c9b99a'} strokeWidth="1.5" />
            {bF && <text x={x} y={C_TOP.y + C_TOP.h + 12 + 3.5} textAnchor="middle" fontSize="6" fontWeight="700" fill="#2d5a2d" fontFamily="sans-serif">{bF.initials}</text>}
          </g>
        );
      })}

      {/* Round tables */}
      {C_ROUNDS.map(t => {
        const seats = cSeats(t.cx, t.cy);
        const sel = selectedId === t.id;
        return (
          <g key={t.id}>
            {sel && <circle cx={t.cx} cy={t.cy} r={C_R + 18} fill="none" stroke="#a68a5b" strokeWidth="1.5" strokeDasharray="4 3" opacity={0.5} />}
            <circle cx={t.cx} cy={t.cy} r={C_R} fill="#f5f0eb" stroke={sel ? '#a68a5b' : '#c9b99a'} strokeWidth={sel ? 2 : 1.5} />
            <text x={t.cx} y={t.cy + 4} textAnchor="middle" fontSize="10" fontWeight="600" fill="#9e8e7a" fontFamily="sans-serif">{t.label}</text>
            {seats.map((s, si) => {
              const f = filledMap.get(`${t.id}-${si}`);
              const isTgt = sel && targetSeat === si;
              return (
                <g key={si}>
                  {isTgt && <circle cx={s.x} cy={s.y} r={C_SR + 5} fill="none" stroke="#a68a5b" strokeWidth="1.5" strokeDasharray="3 2" />}
                  <circle cx={s.x} cy={s.y} r={C_SR}
                    fill={f ? (f.warn ? '#fdecea' : '#eaf2ea') : (isTgt ? '#fdf8f3' : '#fff')}
                    stroke={f ? (f.warn ? '#c0392b' : '#4a7a4a') : (isTgt ? '#a68a5b' : '#c9b99a')}
                    strokeWidth="1.5" />
                  {f && <text x={s.x} y={s.y + 3.5} textAnchor="middle" fontSize="6" fontWeight="700" fill={f.warn ? '#7a1515' : '#2d5a2d'} fontFamily="sans-serif">{f.initials}</text>}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

// ─── App toolbar (realistic) ───────────────────────────────────────────────────

function AppToolbar({ listOpen = false }: { listOpen?: boolean }) {
  return (
    <div style={{ display:'flex', alignItems:'center', padding:'0 10px', height:36, borderBottom:'1px solid #e8e2d9', background:'#fff', flexShrink:0, gap:5 }}>
      <div style={{ display:'flex', flexDirection:'column' }}>
        <span style={{ fontSize:8, color:'#9e8e7a' }}>Emma & James</span>
        <span style={{ fontSize:9, fontWeight:700, color:'#3a2e24' }}>Wedding Seating</span>
      </div>
      <div style={{ display:'flex', gap:3, marginLeft:'auto', alignItems:'center' }}>
        {/* Undo/Redo */}
        {['↩','↪'].map((ic, i) => (
          <div key={i} style={{ width:22, height:22, borderRadius:4, border:'1px solid #e8e2d9', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ fontSize:11, color:'#9e8e7a' }}>{ic}</span>
          </div>
        ))}
        {/* List toggle */}
        <div style={{ width:22, height:22, borderRadius:4, border:'1px solid', borderColor: listOpen ? '#7a6040' : '#e8e2d9', background: listOpen ? '#7a6040' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <span style={{ fontSize:10, color: listOpen ? '#fff' : '#9e8e7a', lineHeight:1 }}>☰</span>
        </div>
        {/* Export */}
        <div style={{ height:22, padding:'0 7px', borderRadius:4, border:'1px solid #e8e2d9', display:'flex', alignItems:'center', gap:3 }}>
          <span style={{ fontSize:8, color:'#7a6040' }}>↓</span>
          <span style={{ fontSize:7.5, color:'#5a4e44', fontWeight:600 }}>Export</span>
        </div>
        {/* Add Table */}
        <div style={{ height:22, padding:'0 7px', borderRadius:4, background:'#7a6040', display:'flex', alignItems:'center', gap:2 }}>
          <span style={{ fontSize:13, color:'#fff', lineHeight:1, marginTop:-1 }}>+</span>
          <span style={{ fontSize:7.5, color:'#fff', fontWeight:700 }}>Table</span>
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar panels ────────────────────────────────────────────────────────────

const UNASSIGNED_GUESTS = [
  { name:'Sophie James', initials:'SJ', rsvp:'yes',   active:true  },
  { name:'Alex Brown',   initials:'AB', rsvp:'yes',   active:false },
  { name:'Mia Taylor',   initials:'MT', rsvp:'yes',   active:false },
  { name:'Ruby Nash',    initials:'RN', rsvp:'maybe', active:false },
  { name:'Ethan Clarke', initials:'EC', rsvp:'yes',   active:false },
  { name:'Isla Reid',    initials:'IR', rsvp:'yes',   active:false },
];

function GuestListSidebar({ remaining }: { remaining: typeof UNASSIGNED_GUESTS }) {
  return (
    <div style={{ width:148, borderLeft:'1px solid #e8e2d9', background:'#fff', display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden' }}>
      <div style={{ padding:'7px 10px 6px', borderBottom:'1px solid #e8e2d9' }}>
        <span style={{ fontSize:8, fontWeight:700, color:'#9e8e7a', textTransform:'uppercase', letterSpacing:0.6 }}>Unassigned</span>
        <span style={{ fontSize:8, color:'#9e8e7a', display:'block', marginTop:1 }}>{remaining.length} guests</span>
      </div>
      <div style={{ overflowY:'auto', flex:1 }}>
        {remaining.map((g, i) => (
          <div key={i} style={{
            padding:'5px 10px',
            background: g.active ? '#f5f0eb' : 'transparent',
            borderLeft: g.active ? '2px solid #a68a5b' : '2px solid transparent',
            display:'flex', alignItems:'center', gap:5,
          }}>
            <div style={{ width:18, height:18, borderRadius:'50%', background: g.rsvp==='yes' ? '#eaf2ea' : '#fef3e2', border:'1px solid', borderColor: g.rsvp==='yes' ? '#4a7a4a' : '#c97c1a', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <span style={{ fontSize:6, fontWeight:700, color: g.rsvp==='yes' ? '#2d5a2d' : '#7a4a0a' }}>{g.initials}</span>
            </div>
            <span style={{ fontSize:8.5, color: g.active ? '#3a2e24' : '#5a5048', fontWeight: g.active ? 700 : 400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{g.name}</span>
            <div style={{ width:6, height:6, borderRadius:'50%', flexShrink:0, background: g.rsvp==='yes' ? '#4a7a4a' : '#c97c1a' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function TableDetailSidebar({ seated, remaining }: {
  seated: { name: string; initials: string }[];
  remaining: { name: string; rsvp: string }[];
}) {
  const total = 6;
  const fillPct = Math.round((seated.length / total) * 100);
  return (
    <div style={{ width:148, borderLeft:'1px solid #e8e2d9', background:'#fff', display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden' }}>
      {/* Header */}
      <div style={{ padding:'7px 10px 6px', borderBottom:'1px solid #e8e2d9' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:3 }}>
          <span style={{ fontSize:10, fontWeight:700, color:'#1a1714' }}>Table 2</span>
          <span style={{ fontSize:7.5, fontWeight:600, color:'#4a7a4a', background:'#eaf2ea', padding:'1px 5px', borderRadius:4 }}>{seated.length}/{total}</span>
        </div>
        <span style={{ fontSize:8, color:'#9e8e7a' }}>Round · 6 seats</span>
        {/* Progress bar */}
        <div style={{ marginTop:5, height:3, background:'#f0ece6', borderRadius:2, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${fillPct}%`, background:'#4a7a4a', borderRadius:2, transition:'width 0.4s ease' }} />
        </div>
      </div>
      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid #e8e2d9' }}>
        {['Guests','Tags','Settings'].map((t, i) => (
          <div key={i} style={{ flex:1, padding:'4px 0', textAlign:'center', fontSize:7, fontWeight: i===0?700:400, color: i===0?'#7a6040':'#9e8e7a', borderBottom: i===0?'1.5px solid #7a6040':'none' }}>{t}</div>
        ))}
      </div>
      {/* Seated */}
      {seated.length > 0 && (
        <div style={{ padding:'4px 0 3px', borderBottom:'1px solid #f0ece6' }}>
          <p style={{ fontSize:7, fontWeight:700, color:'#9e8e7a', textTransform:'uppercase', letterSpacing:0.5, padding:'0 9px 2px' }}>Seated</p>
          {seated.map((g, i) => (
            <div key={i} style={{ padding:'3px 9px', background:'#f4fbf4', display:'flex', alignItems:'center', gap:5, marginBottom:1 }}>
              <div style={{ width:16, height:16, borderRadius:'50%', background:'#eaf2ea', border:'1px solid #4a7a4a', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <span style={{ fontSize:6, fontWeight:700, color:'#2d5a2d' }}>{g.initials}</span>
              </div>
              <span style={{ fontSize:8.5, fontWeight:600, color:'#1a1714', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.name}</span>
              <Check size={8} color="#4a7a4a" />
            </div>
          ))}
        </div>
      )}
      {/* Assign next */}
      <div style={{ padding:'4px 0', flex:1 }}>
        <p style={{ fontSize:7, fontWeight:700, color:'#9e8e7a', textTransform:'uppercase', letterSpacing:0.5, padding:'0 9px 2px' }}>Assign next</p>
        {remaining.slice(0, 4).map((g, i) => (
          <div key={i} style={{ padding:'4px 9px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:4 }}>
            <span style={{ fontSize:8.5, color:'#5a5048', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{g.name}</span>
            <div style={{ width:6, height:6, borderRadius:'50%', flexShrink:0, background: g.rsvp==='yes'?'#4a7a4a':'#c97c1a' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SeatingListPanel() {
  const tables = [
    { name:'Top Table', seated:['EJ','JM','BM'], total:6 },
    { name:'Table 1',   seated:['LB','CP','DM','EW'], total:6 },
    { name:'Table 2',   seated:['SJ','AB','MT','RC','EC'], total:6 },
    { name:'Table 3',   seated:['IR','NK','PJ'], total:6 },
    { name:'Table 4',   seated:['HW','TK'], total:6 },
    { name:'Table 5',   seated:['OL','QP','RS','UV'], total:6 },
  ];
  const totalSeated = tables.reduce((sum, t) => sum + t.seated.length, 0);
  const totalSeats  = tables.reduce((sum, t) => sum + t.total, 0);

  return (
    <div style={{ width:160, borderLeft:'1px solid #e8e2d9', background:'#fff', display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden' }}>
      <div style={{ padding:'7px 10px 6px', borderBottom:'1px solid #e8e2d9' }}>
        <span style={{ fontSize:9, fontWeight:700, color:'#1a1714' }}>Seating List</span>
        <span style={{ fontSize:7.5, color:'#9e8e7a', display:'block', marginTop:1 }}>{totalSeated} of {totalSeats} seated</span>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'4px 0' }}>
        {tables.map((t, i) => (
          <div key={i} style={{ padding:'4px 10px', borderBottom: i < tables.length-1 ? '1px solid #f4f0eb' : 'none' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
              <span style={{ fontSize:8.5, fontWeight:700, color:'#3a2e24' }}>{t.name}</span>
              <span style={{ fontSize:7, color:'#9e8e7a' }}>{t.seated.length}/{t.total}</span>
            </div>
            <div style={{ display:'flex', gap:2, flexWrap:'wrap' }}>
              {t.seated.map((s, j) => (
                <div key={j} style={{ width:16, height:16, borderRadius:'50%', background:'#eaf2ea', border:'1px solid #4a7a4a', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ fontSize:5.5, fontWeight:700, color:'#2d5a2d' }}>{s}</span>
                </div>
              ))}
              {Array.from({ length: t.total - t.seated.length }).map((_, j) => (
                <div key={`e${j}`} style={{ width:16, height:16, borderRadius:'50%', background:'#f5f0eb', border:'1px solid #e0d5c5' }} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding:'6px 10px', borderTop:'1px solid #e8e2d9', background:'#faf8f5' }}>
        <span style={{ fontSize:7.5, fontWeight:700, color:'#4a7a4a' }}>✓ {totalSeated} seated</span>
        <span style={{ fontSize:7, color:'#9e8e7a', marginLeft:8 }}>⚠ 2 dietary</span>
      </div>
    </div>
  );
}

// ─── Hero animation data ───────────────────────────────────────────────────────

// Fills added one-by-one during phase 3 (order matters — most impactful first)
const FILL_SEQUENCE: SeatFill[] = [
  { tableId:2, seat:0, initials:'SJ' },
  { tableId:2, seat:1, initials:'AB' },
  { tableId:2, seat:2, initials:'MT' },
  { tableId:2, seat:4, initials:'EC' },
  { tableId:0, seat:0, initials:'EJ' },
  { tableId:0, seat:1, initials:'JM' },
  { tableId:0, seat:2, initials:'BM' },
  { tableId:1, seat:0, initials:'LB' },
  { tableId:1, seat:2, initials:'CP' },
  { tableId:4, seat:0, initials:'HW' },
  { tableId:3, seat:1, initials:'PJ' },
  { tableId:5, seat:0, initials:'OL' },
];

// ─── Hero animated canvas ──────────────────────────────────────────────────────
// Phases: 0=wizard  1=empty canvas  2=drag  3=fills  4=seating list

export function HeroAnimCanvas() {
  const [phase, setPhase]           = useState(0);
  const [fillCount, setFillCount]   = useState(0);
  const [dragMoved, setDragMoved]   = useState(false);

  // Phase sequencer
  useEffect(() => {
    const DURATIONS = [2600, 700, 1500, 3200, 2800];
    let tid: ReturnType<typeof setTimeout>;

    function advance(current: number) {
      tid = setTimeout(() => {
        const next = (current + 1) % DURATIONS.length;
        setPhase(next);
        setFillCount(0);
        setDragMoved(false);
        advance(next);
      }, DURATIONS[current]);
    }

    advance(0);
    return () => clearTimeout(tid);
  }, []);

  // Drag chip: start moving shortly after phase 2 begins
  useEffect(() => {
    if (phase !== 2) return;
    const tid = setTimeout(() => setDragMoved(true), 200);
    return () => clearTimeout(tid);
  }, [phase]);

  // Progressive seat fills during phase 3
  useEffect(() => {
    if (phase !== 3) return;
    let count = 0;
    const iid = setInterval(() => {
      count++;
      setFillCount(count);
      if (count >= FILL_SEQUENCE.length) clearInterval(iid);
    }, 280);
    return () => clearInterval(iid);
  }, [phase]);

  const currentFills = phase >= 3 ? FILL_SEQUENCE.slice(0, fillCount) : [];
  const showWizard   = phase === 0;
  const showCanvas   = phase >= 1;
  const showDrag     = phase === 2;
  const showListPanel = phase === 4;

  // Unassigned list shrinks as seats fill
  const unassigned = UNASSIGNED_GUESTS.filter(g =>
    !currentFills.some(f => f.initials === g.initials)
  );

  return (
    <div style={{ height:'100%', position:'relative', overflow:'hidden', background:'#faf8f5' }}>

      {/* ── Canvas view (phases 1-4) ── */}
      <div style={{
        position:'absolute', inset:0,
        opacity: showCanvas ? 1 : 0,
        transition: 'opacity 0.6s ease',
        display:'flex', flexDirection:'column',
      }}>
        <AppToolbar listOpen={showListPanel} />
        <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
          <div style={{ flex:1, overflow:'hidden' }}>
            <CanvasSvg
              selectedId={phase === 2 ? 2 : undefined}
              filled={currentFills}
            />
          </div>

          {/* Guest list sidebar — visible phases 1-3 */}
          <div style={{
            opacity: showListPanel ? 0 : 1,
            transition: 'opacity 0.4s ease',
            display:'flex',
          }}>
            {phase <= 1 ? (
              <GuestListSidebar remaining={UNASSIGNED_GUESTS} />
            ) : phase === 2 ? (
              <GuestListSidebar remaining={UNASSIGNED_GUESTS} />
            ) : (
              <TableDetailSidebar
                seated={currentFills.filter(f => f.tableId === 2).map(f => ({
                  initials: f.initials,
                  name: ({ SJ:'Sophie James', AB:'Alex Brown', MT:'Mia Taylor', EC:'Ethan Clarke' } as Record<string, string>)[f.initials] ?? f.initials,
                }))}
                remaining={unassigned.slice(0, 4).map(g => ({ name: g.name, rsvp: g.rsvp }))}
              />
            )}
          </div>

          {/* Seating list panel — phase 4 */}
          <div style={{
            position:'absolute', top:36, right:0, bottom:0,
            opacity: showListPanel ? 1 : 0,
            transform: showListPanel ? 'translateX(0)' : 'translateX(30px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
            pointerEvents: 'none',
          }}>
            <SeatingListPanel />
          </div>
        </div>
      </div>

      {/* ── Drag chip (phase 2) ── */}
      <div style={{
        position:'absolute',
        left:  dragMoved ? '30%' : '74%',
        top:   dragMoved ? '33%' : '22%',
        transition: showDrag
          ? 'left 1.05s cubic-bezier(0.4,0,0.2,1), top 1.05s cubic-bezier(0.4,0,0.2,1)'
          : 'none',
        opacity: showDrag ? 1 : 0,
        transform: 'rotate(-2deg)',
        pointerEvents: 'none', zIndex: 20,
      }}>
        <div style={{
          background:'#fff', border:'1.5px solid #a68a5b', borderRadius:20,
          padding:'4px 10px 4px 7px', display:'flex', alignItems:'center', gap:6,
          boxShadow:'0 6px 20px rgba(0,0,0,0.18)',
        }}>
          <div style={{ width:20, height:20, borderRadius:'50%', background:'#eaf2ea', border:'1.5px solid #4a7a4a', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <span style={{ fontSize:7, fontWeight:700, color:'#2d5a2d' }}>SJ</span>
          </div>
          <span style={{ fontSize:9, fontWeight:700, color:'#3a2e24', whiteSpace:'nowrap' }}>Sophie James</span>
        </div>
      </div>

      {/* ── Wizard overlay (phase 0) ── */}
      <div style={{
        position:'absolute', inset:0,
        opacity: showWizard ? 1 : 0,
        transition: 'opacity 0.5s ease',
        pointerEvents: showWizard ? 'auto' : 'none',
      }}>
        <WizardOverlay />
      </div>

      {/* ── Dots ── */}
      <div style={{ position:'absolute', bottom:8, left:'50%', transform:'translateX(-50%)', display:'flex', gap:4, zIndex:10 }}>
        {[0,1,2,3,4].map(i => (
          <div key={i} style={{
            width: i===phase ? 16 : 5, height:5, borderRadius:3,
            background: i===phase ? '#7a6040' : 'rgba(122,96,64,0.25)',
            transition:'width 0.3s, background 0.3s',
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── Wizard overlay ─────────────────────────────────────────────────────────────

function WizardOverlay() {
  return (
    <div style={{ height:'100%', background:'#faf8f5', position:'relative' }}>
      <svg style={{ position:'absolute', inset:0 }} width="100%" height="100%">
        <defs>
          <pattern id="wbg" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(201,185,154,0.2)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#wbg)" />
      </svg>
      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.35)' }} />
      <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ background:'#fff', borderRadius:14, padding:'22px 26px', width:268, boxShadow:'0 24px 60px rgba(0,0,0,0.22)' }}>
          {/* Step dots */}
          <div style={{ display:'flex', justifyContent:'center', gap:5, marginBottom:16 }}>
            {[0,1,2,3].map(i => (
              <div key={i} style={{ width: i===1?18:6, height:5, borderRadius:3, background: i===1?'#7a6040':'#e0d8cf' }} />
            ))}
          </div>
          <p style={{ fontSize:14, fontWeight:700, color:'#1a1714', textAlign:'center', marginBottom:3 }}>How many tables?</p>
          <p style={{ fontSize:10, color:'#9e8e7a', textAlign:'center', marginBottom:16 }}>You can always add or remove later</p>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, marginBottom:11 }}>
            <div style={{ width:28, height:28, borderRadius:'50%', border:'1px solid #c9b99a', display:'flex', alignItems:'center', justifyContent:'center', color:'#7a6040', fontSize:16 }}>−</div>
            <span style={{ fontSize:40, fontWeight:800, color:'#1a1714', width:48, textAlign:'center', fontVariantNumeric:'tabular-nums' }}>8</span>
            <div style={{ width:28, height:28, borderRadius:'50%', border:'1px solid #c9b99a', display:'flex', alignItems:'center', justifyContent:'center', color:'#7a6040', fontSize:16 }}>+</div>
          </div>
          <div style={{ margin:'0 14px 16px', height:4, background:'#e8e2d9', borderRadius:2, position:'relative' }}>
            <div style={{ width:'27%', height:'100%', background:'#7a6040', borderRadius:2 }} />
            <div style={{ position:'absolute', left:'27%', top:-4, width:12, height:12, background:'#7a6040', borderRadius:'50%', transform:'translateX(-50%)', boxShadow:'0 1px 4px rgba(0,0,0,0.2)' }} />
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <div style={{ flex:1, height:30, borderRadius:8, border:'1px solid #c9b99a', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:10, color:'#7a6040' }}>← Back</span>
            </div>
            <div style={{ flex:1, height:30, borderRadius:8, background:'#7a6040', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:10, color:'#fff', fontWeight:700 }}>Next →</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Chapter screens (for pinned scroll section) ───────────────────────────────

const CHAPTER2_FILLS: SeatFill[] = [
  { tableId:0, seat:0, initials:'EJ' }, { tableId:0, seat:1, initials:'JM' }, { tableId:0, seat:2, initials:'BM' },
  { tableId:1, seat:0, initials:'LB' }, { tableId:1, seat:1, initials:'CP' }, { tableId:1, seat:2, initials:'DM' }, { tableId:1, seat:3, initials:'EW' },
  { tableId:2, seat:0, initials:'SJ' }, { tableId:2, seat:1, initials:'AB' }, { tableId:2, seat:2, initials:'MT' },
  { tableId:4, seat:0, initials:'HW' },
];

const CHAPTER3_FILLS: SeatFill[] = [
  ...CHAPTER2_FILLS,
  { tableId:2, seat:3, initials:'RC' }, { tableId:2, seat:4, initials:'EC' },
  { tableId:3, seat:0, initials:'IR' }, { tableId:3, seat:1, initials:'NK' }, { tableId:3, seat:2, initials:'PJ' },
  { tableId:4, seat:1, initials:'TK' },
  { tableId:5, seat:0, initials:'OL' }, { tableId:5, seat:1, initials:'QP' }, { tableId:5, seat:2, initials:'RS' }, { tableId:5, seat:3, initials:'UV' },
];

export function ChapterScene1() {
  return <WizardOverlay />;
}

export function ChapterScene2() {
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <AppToolbar />
      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
        <div style={{ flex:1, overflow:'hidden' }}>
          <CanvasSvg selectedId={2} filled={CHAPTER2_FILLS} />
        </div>
        <TableDetailSidebar
          seated={[
            { initials:'SJ', name:'Sophie James' },
            { initials:'AB', name:'Alex Brown' },
            { initials:'MT', name:'Mia Taylor' },
          ]}
          remaining={[
            { name:'Ethan Clarke', rsvp:'yes' },
            { name:'Ruby Nash',    rsvp:'maybe' },
            { name:'Isla Reid',    rsvp:'yes' },
          ]}
        />
      </div>
    </div>
  );
}

export function ChapterScene3() {
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <AppToolbar listOpen />
      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
        <div style={{ flex:1, overflow:'hidden' }}>
          <CanvasSvg filled={CHAPTER3_FILLS} />
        </div>
        <SeatingListPanel />
      </div>
    </div>
  );
}
